import { Lead, LeadStatus, ProjectStatus } from '../types';

/** Ordre d'affichage des statuts lead : workflow puis clôture (abandonné, refusé en dernier). Source unique pour listes, filtres, pipeline, counts. */
export const LEAD_STATUS_DISPLAY_ORDER: LeadStatus[] = [
  'to_contact',
  'pending',
  'negotiation',
  'in_production',
  'completed',
  'abandoned',
  'rejected',
];

/** Clés i18n pour chaque statut lead (status.xxx). Source unique pour badges, menus, pipeline, modals. */
export const LEAD_STATUS_I18N_KEYS: Record<LeadStatus, string> = {
  to_contact: 'status.toContact',
  pending: 'status.pending',
  negotiation: 'status.negotiation',
  in_production: 'status.inProduction',
  completed: 'status.completed',
  abandoned: 'status.abandoned',
  rejected: 'status.rejected',
};

/** Statuts de lead gérés automatiquement par le workflow projet (pas de "Passer à" manuel). */
export const LEAD_STATUS_AUTOMATIC: LeadStatus[] = ['in_production', 'completed'];

export function isAutomaticLeadStatus(lead: Pick<Lead, 'status'> | null | undefined): boolean {
  const s = String(lead?.status ?? '').toLowerCase().trim();
  return LEAD_STATUS_AUTOMATIC.some((status) => status === s);
}

/**
 * Retourne le statut suivant pour un lead, ou null si c'est un statut final.
 * Flow = progression métier uniquement (pas abandoned/rejected).
 */
export const getNextLeadStatus = (currentStatus: LeadStatus): LeadStatus | null => {
  const statusFlow: LeadStatus[] = [
    'to_contact',
    'pending',
    'negotiation',
    'in_production',
    'completed',
  ];

  const currentIndex = statusFlow.indexOf(currentStatus);
  
  // Si le statut n'est pas dans le flow ou est le dernier, retourner null
  if (currentIndex === -1 || currentIndex === statusFlow.length - 1) {
    return null;
  }

  return statusFlow[currentIndex + 1];
};

/**
 * Retourne le statut suivant pour un projet, ou null si c'est un statut final
 */
export const getNextProjectStatus = (currentStatus: ProjectStatus): ProjectStatus | null => {
  const statusFlow: ProjectStatus[] = [
    'BRIEF_RECEIVED',
    'IN_PRODUCTION',
    'V1_SENT',
    'CHANGES_REQUESTED',
    'DELIVERED',
    'ARCHIVED',
  ];

  const currentIndex = statusFlow.indexOf(currentStatus);
  
  // Si le statut n'est pas dans le flow ou est le dernier, retourner null
  if (currentIndex === -1 || currentIndex === statusFlow.length - 1) {
    return null;
  }

  return statusFlow[currentIndex + 1];
};

/** Statuts hors quota « leads actifs » (clôture pipeline). */
export const LEAD_INACTIVE_STATUSES: LeadStatus[] = [
  'rejected',
  'abandoned',
  'completed',
];

/** Statuts froids candidats à l’abandon one-click (jamais in_production). */
export const LEAD_ABANDON_SUGGESTION_STATUSES: LeadStatus[] = [
  'to_contact',
  'pending',
  'negotiation',
];

export function isActiveLeadStatus(status: LeadStatus | string): boolean {
  return !LEAD_INACTIVE_STATUSES.includes(status as LeadStatus);
}

/**
 * Active pipeline order for “suggest archive” (most advanced first).
 * Higher index = better archive candidate. ARCHIVED is excluded.
 */
export const PROJECT_ARCHIVE_SUGGESTION_ORDER: ProjectStatus[] = [
  'BRIEF_RECEIVED',
  'IN_PRODUCTION',
  'V1_SENT',
  'CHANGES_REQUESTED',
  'DELIVERED',
];

export function getProjectArchiveSuggestionRank(status: ProjectStatus | string): number {
  const idx = PROJECT_ARCHIVE_SUGGESTION_ORDER.indexOf(status as ProjectStatus);
  return idx === -1 ? -1 : idx;
}
